// DA-15309: цветовая легенда bldg_class, общая для слоёв «Типология застройки» и «Типология
// территорий» (изначально был 1 слой с под-слоями Здания/Территории и переключателями в легенде,
// разделён на 2 независимых слоя с собственными чекбоксами в сайдбаре — переключатели убраны).
// Тумблер 3D — только для «Типология застройки»: включает компаньон-слой экструзии зданий по
// этажности вместо плоского fill (см. спец-кейсы toggleLayer/componentDidUpdate в Map.jsx).
// Тумблеры «Типология зон» (заливка территорий, по умолчанию включена) и «Границы кварталов»
// (подслой-обводка квартальной сетки dwh.quaters_generated, из которой нарезаны территории,
// по умолчанию выключен) — только для слоя «Типология территорий». Выключив первый и включив
// второй, можно смотреть одну квартальную сетку без раскраски.
// Радиокнопки покраски — тоже только для зданий: этажности и населения у территорий нет,
// они всегда красятся по классу (см. applyUrbanTypologyColor в Map.jsx).
import { t } from '@lingui/macro';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import {
    onChangeMapFilters,
    onToggleUrbanTypologyExtrudeBuildings,
    onToggleUrbanTypologyShowBlocks,
    onToggleUrbanTypologyShowZoneFill,
} from '../../../ac/onChangeMapFilters';
import {
    URBAN_TYPOLOGY_LEVELS_STOPS,
    URBAN_TYPOLOGY_POPULATION_STOPS,
    URBAN_TYPOLOGY_NO_DATA_COLOR,
} from '../../../constants/layers';
import { StandardRadioGroup } from '../StandardRadioGroup';
import { LegendIconRenderer } from '../../LegendRenderer/LegendIconRenderer';
import { LEGEND_ICON_TYPES } from '../../../types/legendTypes';
import styles from '../FilterSidebar.module.css';

const CLASS_ITEMS = [
    { value: 'residential_individual', color: '#FACC15', getLabel: () => t`Индивидуальные жилые дома` },
    { value: 'residential_multi', color: '#F97316', getLabel: () => t`Многоквартирные жилые дома` },
    { value: 'commercial', color: '#EF4444', getLabel: () => t`Коммерческая застройка` },
    { value: 'education', color: '#EC4899', getLabel: () => t`Образование и воспитание` },
    { value: 'campus', color: '#BE123C', getLabel: () => t`Кампусы университетов` },
    // DA-15202: green_park стоит выше green — от благоустроенного к неблагоустроенному.
    // Цвета парные по насыщенности, см. URBAN_TYPOLOGY_COLOR в layers.js.
    { value: 'green_park', color: '#22C55E', getLabel: () => t`Благоустроенное озеленение` },
    { value: 'green', color: '#84CC16', getLabel: () => t`Зелёные территории` },
    { value: 'healthcare', color: '#0EA5E9', getLabel: () => t`Здравоохранение` },
    { value: 'sport', color: '#6366F1', getLabel: () => t`Спорт` },
    { value: 'transport_hub', color: '#A855F7', getLabel: () => t`Транспортные хабы` },
    { value: 'industrial', color: '#D4D4D8', getLabel: () => t`Промышленно-коммунальная застройка` },
    { value: 'service', color: '#FFFFFF', getLabel: () => t`Служебные постройки` },
    // Одна строка на три значения: 'unclassified' у территорий, 'прочее' и NULL у зданий —
    // для читателя легенды это одно состояние «класс неизвестен».
    { value: 'unclassified', color: '#14B8A6', getLabel: () => t`Не классифицировано` },
];
const BUILDINGS_LAYER_ID = 'Типология застройки';
const ZONES_LAYER_ID = 'Типология территорий';
const COLOR_MODES = [
    { value: 'class', getLabel: () => t`Класс здания` },
    { value: 'levels', getLabel: () => t`Этажность` },
    { value: 'population', getLabel: () => t`Численность населения` },
];
const STOPS_BY_MODE = {
    levels: URBAN_TYPOLOGY_LEVELS_STOPS,
    population: URBAN_TYPOLOGY_POPULATION_STOPS,
};
// Стопы шкалы нелинейны по значению (пороги подобраны по перцентилям, см. layers.js), поэтому в
// полосе они распределяются равномерно, а не пропорционально значению: иначе жёлтый край
// населения схлопнулся бы в пару пикселей. Подписи стоят ровно под своими стопами.
const GradientScale = ({ stops }) => {
    const gradient = stops
        .map(({ color }, i) => `${color} ${(i / (stops.length - 1)) * 100}%`)
        .join(', ');
    return (
        <div style={{ padding: '0 10px 4px' }}>
            <div
                style={{
                    height: '14px',
                    borderRadius: '2px',
                    border: '1px solid rgba(0, 0, 0, 0.2)',
                    background: `linear-gradient(to right, ${gradient})`,
                }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                {stops.map(({ value }, i) => (
                    <span
                        key={value}
                        style={{ fontSize: '11px' }}
                    >
                        {i === stops.length - 1 ? `${value}+` : value}
                    </span>
                ))}
            </div>
        </div>
    );
};
const ClassItems = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr', columnGap: '12px', rowGap: '8px', alignItems: 'center', padding: '10px' }}>
        {CLASS_ITEMS.map(({ value, color, getLabel }) => (
            <React.Fragment key={value ?? 'null'}>
                <LegendIconRenderer
                    type={LEGEND_ICON_TYPES.RECT}
                    color={color}
                    size="16px"
                    opacity={0.9}
                />
                <p style={{ margin: 0 }}>{getLabel()}</p>
            </React.Fragment>
        ))}
    </div>
);
const UrbanTypologyLegendComponent = ({
    displayedLayers,
    mapFilters,
    onChangeMapFilters: onChangeFilters,
    onToggleUrbanTypologyExtrudeBuildings: onToggleExtrude,
    onToggleUrbanTypologyShowBlocks: onToggleBlocks,
    onToggleUrbanTypologyShowZoneFill: onToggleZoneFill,
}) => {
    const isBuildingsDisplayed = displayedLayers.some(({ id }) => id === BUILDINGS_LAYER_ID);
    const isZonesDisplayed = displayedLayers.some(({ id }) => id === ZONES_LAYER_ID);
    if (!isBuildingsDisplayed && !isZonesDisplayed) return null;
    const colorMode = mapFilters.urbanTypologyColorMode ?? 'class';
    const handleColorModeChange = ({ target: { value } }) => {
        onChangeFilters('urbanTypologyColorMode', value);
    };
    // Шкала показывается, только когда здания реально покрашены по этому атрибуту. Список классов —
    // когда по классу покрашено хоть что-то: территории красятся по классу всегда, независимо от
    // выбранного для зданий режима, поэтому при включённых территориях список нужен и в режиме
    // этажности/населения.
    const scaleStops = isBuildingsDisplayed ? STOPS_BY_MODE[colorMode] : null;
    const showClassItems = isZonesDisplayed || colorMode === 'class';
    return (
        <div>
            {isBuildingsDisplayed && (
                <>
                    <div className={styles.errGroupHeader}>
                        <span style={{ fontSize: '15px' }}>{t`Объём по этажности (3D)`}</span>
                        <label className={styles.switch}>
                            <input
                                type='checkbox'
                                checked={mapFilters.urbanTypologyExtrudeBuildings}
                                onChange={onToggleExtrude}
                            />
                            <span className={classnames(styles.sliderRound, styles.slider)} />
                        </label>
                    </div>
                    <div className={styles.legHead}>{t`Покраска зданий`}:</div>
                    <StandardRadioGroup
                        value={colorMode}
                        onChange={handleColorModeChange}
                    >
                        {COLOR_MODES.map(({ value, getLabel }) => (
                            <FormControlLabel
                                key={value}
                                className={styles.isochroneFilterLabel}
                                control={
                                    <Radio
                                        checked={colorMode === value}
                                        onChange={handleColorModeChange}
                                        value={value}
                                        className={classnames(
                                            styles.isochroneFilterInput, {
                                                [styles.checkedIsochroneFilterInput]: colorMode === value,
                                            }
                                        )}
                                        size="small"
                                    />
                                }
                                label={getLabel()}
                            />
                        ))}
                    </StandardRadioGroup>
                </>
            )}
            {isZonesDisplayed && (
                <div className={styles.errGroupHeader}>
                    <span style={{ fontSize: '15px' }}>{t`Типология зон`}</span>
                    <label className={styles.switch}>
                        <input
                            type='checkbox'
                            checked={mapFilters.urbanTypologyShowZoneFill}
                            onChange={onToggleZoneFill}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            )}
            {isZonesDisplayed && (
                <div className={styles.errGroupHeader}>
                    <span style={{ fontSize: '15px' }}>{t`Границы кварталов`}</span>
                    <label className={styles.switch}>
                        <input
                            type='checkbox'
                            checked={mapFilters.urbanTypologyShowBlocks}
                            onChange={onToggleBlocks}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            )}
            {scaleStops && (
                <>
                    <div className={styles.legHead}>
                        {colorMode === 'levels' ? t`Этажей` : t`Жителей`}:
                    </div>
                    <GradientScale stops={scaleStops} />
                    <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr', columnGap: '12px', alignItems: 'center', padding: '4px 10px 10px' }}>
                        <LegendIconRenderer
                            type={LEGEND_ICON_TYPES.RECT}
                            color={URBAN_TYPOLOGY_NO_DATA_COLOR}
                            size="16px"
                            opacity={0.9}
                        />
                        <p style={{ margin: 0 }}>{t`Нет данных`}</p>
                    </div>
                </>
            )}
            {showClassItems && (
                <>
                    <div className={styles.legHead}>{t`Типы застройки`}:</div>
                    <ClassItems />
                </>
            )}
        </div>
    );
};
const mapStateToProps = ({ displayedLayers, mapFilters }) => ({ displayedLayers, mapFilters });

export const UrbanTypologyLegend = connect(mapStateToProps, {
    onChangeMapFilters,
    onToggleUrbanTypologyExtrudeBuildings,
    onToggleUrbanTypologyShowBlocks,
    onToggleUrbanTypologyShowZoneFill,
})(UrbanTypologyLegendComponent);
